import React, { useState } from 'react';
import { Users, Shield } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import GestionUsuarios from './GestionUsuarios';
import GestionRoles from './GestionRoles';

type VistaAdministracion = 'usuarios' | 'roles';

const Administracion: React.FC = () => {
  const [vistaActiva, setVistaActiva] =
    useState<VistaAdministracion>('usuarios');

  const tabs = [
    {
      id: 'usuarios' as const,
      label: 'Gestión de Usuarios',
      icon: Users,
      description: 'Administrar usuarios del sistema',
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800',
    },
    {
      id: 'roles' as const,
      label: 'Gestión de Roles',
      icon: Shield,
      description: 'Administrar roles y permisos',
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      borderColor: 'border-purple-200 dark:border-purple-800',
    },
  ];

  const tabActiva = tabs.find((tab) => tab.id === vistaActiva);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader
        title="Administración"
        helpSeccion="administracion"
        subtitle="Gestión de usuarios, roles y permisos del sistema"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = vistaActiva === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => setVistaActiva(tab.id)}
                    className={`
                      group inline-flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                      ${
                        isActive
                          ? `${tab.color} border-current`
                          : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      }
                    `}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Tab Header */}
          {tabActiva && (
            <div
              className={`rounded-lg p-6 border ${tabActiva.bgColor} ${tabActiva.borderColor}`}
            >
              <div className="flex items-center space-x-3">
                <div
                  className={`p-2 rounded-lg bg-white dark:bg-gray-800 ${tabActiva.color}`}
                >
                  <tabActiva.icon className="h-6 w-6" />
                </div>
                <div>
                  <h2 className={`text-xl font-semibold ${tabActiva.color}`}>
                    {tabActiva.label}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {tabActiva.description}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Content por vista */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            {vistaActiva === 'usuarios' && <GestionUsuarios embebido={true} />}
            {vistaActiva === 'roles' && <GestionRoles embebido={true} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Administracion;
